import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Card, Col, Row, Table, Button, Form, InputGroup } from 'react-bootstrap';
import Select from 'react-select';
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";

function OpeningStockView() {
    const navigate = useNavigate();
    const [openingStocks, setOpeningStocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [warehouseFilter, setWarehouseFilter] = useState(null);
    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [dateFilter, setDateFilter] = useState('');

    // 🔹 Fetch All Warehouses for filter
    const getWarehouseData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}warehouse/findAllWarehouse`, {
                headers: authHeader()
            });
            const data = response.data || [];
            const options = data.map(w => ({
                value: w.id,
                label: w.name || w.warehousename
            }));
            setWarehouseOptions(options);
        } catch (error) {
            console.error('Error fetching warehouses:', error);
        }
    };

    // 🔹 Fetch Opening Stocks
    const getOpeningStocks = async (page = 1) => {
        setLoading(true);
        try {
            let url = `${BASE_URL}"openingstock/findById"{id}`;
            
            // Add filters if provided
            const params = [];
            if (searchTerm) params.push(`search=${encodeURIComponent(searchTerm)}`);
            if (warehouseFilter) params.push(`warehouseId=${warehouseFilter.value}`);
            if (dateFilter) params.push(`date=${dateFilter}`);
            
            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const response = await axios.get(url, {
                headers: authHeader()
            });

            const data = response.data;
            setOpeningStocks(data?.Index || data?.data || []);
            setTotalPages(data?.totalPages || 1);
            setCurrentPage(page);
        } catch (error) {
            console.error('Error fetching opening stocks:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch opening stocks.'
            });
        } finally {
            setLoading(false);
        }
    };

    // 🔹 Delete Opening Stock
    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(`${BASE_URL}openingstock/delete/${id}`, {
                    headers: authHeader()
                });
                
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Opening stock has been deleted.'
                });
                
                getOpeningStocks(currentPage);
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.response?.data || 'Failed to delete opening stock.'
                });
            }
        }
    };

    // 🔹 Apply Filters
    const handleSearch = () => {
        setCurrentPage(1);
        getOpeningStocks(1);
    };

    // 🔹 Clear Filters
    const handleClearFilters = () => {
        setSearchTerm('');
        setWarehouseFilter(null);
        setDateFilter('');
        setCurrentPage(1);
        getOpeningStocks(1);
    };

    useEffect(() => {
        getWarehouseData();
        getOpeningStocks(1);
    }, []);

    return (
        <div className="container mt-4">
            <Card className="shadow-sm">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Opening Stock List</h5>
                    <Link to="/opening-stock/create" className="btn btn-primary">
                        Add Opening Stock
                    </Link>
                </Card.Header>
                <Card.Body>
                    {/* Filters */}
                    <Row className="mb-3">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Search</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by product..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Warehouse</Form.Label>
                                <Select
                                    value={warehouseFilter}
                                    onChange={setWarehouseFilter}
                                    options={warehouseOptions}
                                    placeholder="Filter by warehouse"
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={dateFilter}
                                    onChange={(e) => setDateFilter(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3} className="d-flex align-items-end">
                            <Button variant="primary" onClick={handleSearch} className="me-2">
                                Search
                            </Button>
                            <Button variant="secondary" onClick={handleClearFilters}>
                                Clear
                            </Button>
                        </Col>
                    </Row>

                    {/* Opening Stocks Table */}
                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <Table striped bordered hover>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Product</th>
                                            <th>Warehouse</th>
                                            <th>Quantity</th>
                                            <th>Opening Date</th>
                                            <th>Created Date</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {openingStocks.length > 0 ? (
                                            openingStocks.map((stock, index) => (
                                                <tr key={stock.id}>
                                                    <td>{(currentPage - 1) * 10 + index + 1}</td>
                                                    <td>
                                                        {stock.product?.description || 'N/A'}
                                                        {stock.product?.serialnumber && (
                                                            <small className="text-muted d-block">
                                                                SN: {stock.product.serialnumber}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td>{stock.warehouse?.name || stock.warehouse?.warehousename || 'N/A'}</td>
                                                    <td>{stock.quantity}</td>
                                                    <td>{stock.openingdate ? new Date(stock.openingdate).toLocaleDateString() : 'N/A'}</td>
                                                    <td>{stock.createdDate ? new Date(stock.createdDate).toLocaleDateString() : 'N/A'}</td>
                                                    <td>
                                                        <div className="btn-group" role="group">
                                                            <Link
                                                                to={`/opening-stock/edit/${stock.id}`}
                                                                className="btn btn-warning btn-sm"
                                                            >
                                                                Edit
                                                            </Link>
                                                            <button
                                                                className="btn btn-danger btn-sm"
                                                                onClick={() => handleDelete(stock.id)}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-3">
                                                    No opening stocks found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div>
                                        Page {currentPage} of {totalPages}
                                    </div>
                                    <div>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            disabled={currentPage === 1}
                                            onClick={() => getOpeningStocks(currentPage - 1)}
                                            className="me-2"
                                        >
                                            Previous
                                        </Button>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            disabled={currentPage === totalPages}
                                            onClick={() => getOpeningStocks(currentPage + 1)}
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
}

export default OpeningStockView;